import { z } from "zod";

import { buildAppLink } from "./buildAppLink";
import { catalogIdSchema } from "./catalogId";
import { formatCategoryWithOtherEnum } from "./formatGroups";
import { offerConditionSchema } from "./offerConditions";
import { taxonomyCatalogByLocale } from "./taxonomyCatalog";

const genreKeySchema = z
  .string()
  .refine((key) => Object.hasOwn(taxonomyCatalogByLocale.en.genres, key), {
    message: "Expected an exact catalog genre key",
  });
const styleKeySchema = z
  .string()
  .refine((key) => Object.hasOwn(taxonomyCatalogByLocale.en.styles, key), {
    message: "Expected an exact catalog style key",
  });

/** Supported original-year bounds; the page closes an open range with them. */
export const OFFER_YEAR_MIN = 1900;
export const OFFER_YEAR_MAX = 2100;
const offerYearSchema = z
  .number()
  .int()
  .min(OFFER_YEAR_MIN)
  .max(OFFER_YEAR_MAX)
  .nullish()
  .transform((value) => value ?? undefined);

export const offersPageFiltersSchema = z
  .object({
    condition: offerConditionSchema.default("new"),
    format: z.union([formatCategoryWithOtherEnum, z.literal("all")]).default("all"),
    artistIds: z
      .array(catalogIdSchema)
      .min(1)
      .max(20)
      .nullish()
      .transform((value) => value ?? undefined)
      .describe(
        "Exact catalog artist IDs from catalog search. Values use OR; null means no artist restriction.",
      ),
    genre: z
      .array(genreKeySchema)
      .nullish()
      .transform((value) => value ?? undefined)
      .describe(
        "Exact canonical genre keys from getOfferFilters or searchOffers. Values use OR; null means no genre restriction.",
      ),
    style: z
      .array(styleKeySchema)
      .nullish()
      .transform((value) => value ?? undefined)
      .describe(
        "Exact canonical style keys from getOfferFilters or searchOffers. Values use OR; null means no style restriction.",
      ),
    priceMin: z
      .number()
      .min(0)
      .nullish()
      .transform((value) => value ?? undefined)
      .describe("Null unless the user explicitly requested a minimum price."),
    priceMax: z
      .number()
      .min(0)
      .nullish()
      .transform((value) => value ?? undefined)
      .describe("Null unless the user explicitly requested a maximum price."),
    yearFrom: offerYearSchema.describe(
      "Earliest original release year of the music, inclusive; null means no lower bound.",
    ),
    yearTo: offerYearSchema.describe(
      "Latest original release year of the music, inclusive; null means no upper bound.",
    ),
    sort: z.enum(["best_price", "price_drop", "recent"]).default("price_drop"),
  })
  .strict()
  .refine(
    (input) =>
      input.priceMin === undefined ||
      input.priceMax === undefined ||
      input.priceMin <= input.priceMax,
    {
      message: "Minimum price must not exceed maximum price",
    },
  )
  .refine(
    (input) =>
      input.yearFrom === undefined || input.yearTo === undefined || input.yearFrom <= input.yearTo,
    {
      message: "Earliest year must not exceed latest year",
    },
  );

/**
 * The page reads one `year` value as that exact year and two as a closed range,
 * so an open bound is closed at the supported year limit.
 */
function offersPageYearParam(yearFrom: number | undefined, yearTo: number | undefined) {
  if (yearFrom === undefined && yearTo === undefined) return undefined;
  const from = yearFrom ?? OFFER_YEAR_MIN;
  const to = yearTo ?? OFFER_YEAR_MAX;
  return from === to ? [from] : [from, to];
}

/**
 * Builds a first-party offers page using only filters supported by the page. The page's
 * defaults (`format=all`, `sort=price_drop`) stay out of the URL, and the params follow the
 * offers canonical order, so an indexable filter set links straight to its canonical URL.
 */
export function buildOffersPageLink(rawInput: z.input<typeof offersPageFiltersSchema>) {
  const input = offersPageFiltersSchema.parse(rawInput);
  return buildAppLink(["offers"], {
    query: {
      condition: input.condition === "used" ? "used" : undefined,
      artistId: input.artistIds,
      format: input.format === "all" ? undefined : input.format,
      genre: input.genre,
      style: input.style,
      year: offersPageYearParam(input.yearFrom, input.yearTo),
      priceMin: input.priceMin,
      priceMax: input.priceMax,
      sort: input.sort === "price_drop" ? undefined : input.sort,
    },
  });
}
